/**
 * Change Password Modal
 * 
 * Shown when user logs in with a temporary password (must_change_password flag).
 * Forces user to set a new password before accessing the app.
 */

import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";

// ============================================================
// STYLES
// ============================================================
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    backdropFilter: "blur(4px)",
  },
  modal: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    borderRadius: 20,
    padding: "40px 48px",
    maxWidth: 420,
    width: "90%",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  glow: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: 200,
    height: 200,
    background: "radial-gradient(circle, rgba(251, 191, 36, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  emoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "#fff",
    margin: "0 0 8px",
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
  },
  subtitle: {
    fontSize: 14,
    color: "rgba(255, 255, 255, 0.6)",
    margin: "0 0 28px",
    lineHeight: 1.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  inputWrapper: {
    position: "relative",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 12,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    color: "#fff",
    fontSize: 16,
    outline: "none",
    transition: "all 0.2s ease",
    boxSizing: "border-box",
  },
  inputFocused: {
    border: "1px solid rgba(47, 214, 181, 0.5)",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
  button: {
    width: "100%",
    padding: "14px 24px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg, #2fd6b5 0%, #26b89a 100%)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  error: {
    color: "#ef4444",
    fontSize: 13,
    marginTop: 8,
    textAlign: "center",
  },
  requirements: {
    fontSize: 12,
    color: "rgba(255, 255, 255, 0.5)",
    textAlign: "left",
    marginTop: 4,
    lineHeight: 1.4,
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 4,
  },
  checkIcon: {
    width: 14,
    height: 14,
  },
};

// ============================================================
// COMPONENT
// ============================================================
export default function ChangePasswordModal() {
  const { updatePassword, userEmail } = useAuth();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState(null);

  // Password requirements
  const hasMinLength = password.length >= 8;
  const hasNumber = /\d/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isValid = hasMinLength && hasNumber && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || saving) return;

    setSaving(true);
    setError("");

    try {
      await updatePassword(password);
      // Modal will auto-dismiss when mustChangePassword becomes false
    } catch (err) {
      console.error("Failed to change password:", err);
      setError(err.message || "Failed to change password. Please try again.");
      setSaving(false);
    }
  };

  const CheckIcon = ({ checked }) => (
    <svg 
      style={{ ...styles.checkIcon, color: checked ? "#22c55e" : "rgba(255,255,255,0.3)" }}
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      {checked ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      ) : (
        <circle cx="12" cy="12" r="8" strokeWidth={2} />
      )}
    </svg>
  );

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.glow} />
        
        <div style={styles.emoji}>🔐</div>
        
        <h2 style={styles.title}>Set Your New Password</h2>
        <p style={styles.subtitle}>
          You're signed in as <strong style={{ color: "#2fd6b5" }}>{userEmail}</strong>
          <br />
          Please create a new password to continue.
        </p>

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputWrapper}>
            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusedField("password")}
              onBlur={() => setFocusedField(null)}
              style={{
                ...styles.input,
                ...(focusedField === "password" ? styles.inputFocused : {}),
              }}
              autoFocus
            />
          </div>

          <div style={styles.inputWrapper}>
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={() => setFocusedField("confirm")}
              onBlur={() => setFocusedField(null)}
              style={{
                ...styles.input,
                ...(focusedField === "confirm" ? styles.inputFocused : {}),
              }}
            />
          </div>

          <div style={styles.requirements}>
            <div style={styles.checkItem}>
              <CheckIcon checked={hasMinLength} />
              <span style={{ color: hasMinLength ? "#22c55e" : "inherit" }}>At least 8 characters</span>
            </div>
            <div style={styles.checkItem}>
              <CheckIcon checked={hasNumber} />
              <span style={{ color: hasNumber ? "#22c55e" : "inherit" }}>Contains a number</span>
            </div>
            <div style={styles.checkItem}>
              <CheckIcon checked={passwordsMatch} />
              <span style={{ color: passwordsMatch ? "#22c55e" : "inherit" }}>Passwords match</span>
            </div>
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            disabled={!isValid || saving}
            style={{
              ...styles.button,
              ...(!isValid || saving ? styles.buttonDisabled : {}),
            }}
          >
            {saving ? "Saving..." : "Set Password & Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
